import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface Supplier {
  id: number;
  name: string;
  contact_person: string;
  email: string;
  phone: string;
  whatsapp: string;
  lead_time_days: number | null;
  preferred_order_channel: string;
  preferred_order_channel_display: string;
  delivery_days: string;
  min_order_value: string | null;
  min_order_currency: string;
  payment_terms: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export const ORDER_CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'portal', label: 'Supplier Portal' },
  { value: 'other', label: 'Other' },
];

export const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'TRY', 'CHF'];

export const DAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const emptyForm = {
  name: '',
  contact_person: '',
  email: '',
  phone: '',
  whatsapp: '',
  lead_time_days: '',
  preferred_order_channel: '',
  delivery_days: [] as string[],
  min_order_value: '',
  min_order_currency: 'EUR',
  payment_terms: '',
  notes: '',
};

export type SupplierForm = typeof emptyForm;

export function useCompanySuppliers() {
  const { isExpired } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [form, setForm] = useState<SupplierForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; supplier: Supplier | null; deleting: boolean;
  }>({ showModal: false, supplier: null, deleting: false });

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await companyAPI.getSuppliers();
      setSuppliers(res.data);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const filtered = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.contact_person.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenCreate = () => {
    setEditingSupplier(null);
    setForm(emptyForm);
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setForm({
      name: supplier.name,
      contact_person: supplier.contact_person,
      email: supplier.email,
      phone: supplier.phone,
      whatsapp: supplier.whatsapp,
      lead_time_days: supplier.lead_time_days != null ? String(supplier.lead_time_days) : '',
      preferred_order_channel: supplier.preferred_order_channel,
      delivery_days: supplier.delivery_days ? supplier.delivery_days.split(',').filter(Boolean) : [],
      min_order_value: supplier.min_order_value ?? '',
      min_order_currency: supplier.min_order_currency,
      payment_terms: supplier.payment_terms,
      notes: supplier.notes,
    });
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const toggleDeliveryDay = (day: string) => {
    setForm(prev => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter(d => d !== day)
        : [...prev.delivery_days, day],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFeedback({ error: 'Supplier name is required.', success: '' });
      return;
    }
    setSaving(true);
    setFeedback({ error: '', success: '' });
    const payload: any = {
      name: form.name.trim(),
      contact_person: form.contact_person,
      email: form.email,
      phone: form.phone,
      whatsapp: form.whatsapp,
      preferred_order_channel: form.preferred_order_channel,
      delivery_days: form.delivery_days.join(','),
      min_order_currency: form.min_order_currency,
      payment_terms: form.payment_terms,
      notes: form.notes,
      lead_time_days: form.lead_time_days !== '' ? form.lead_time_days : null,
      min_order_value: form.min_order_value !== '' ? form.min_order_value : null,
    };
    try {
      if (editingSupplier) {
        const res = await companyAPI.updateSupplier(editingSupplier.id, payload);
        setSuppliers(prev => prev.map(s => s.id === editingSupplier.id ? res.data : s));
      } else {
        const res = await companyAPI.createSupplier(payload);
        setSuppliers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowModal(false);
      setFeedback({ error: '', success: editingSupplier ? 'Supplier updated.' : 'Supplier added.' });
    } catch (err: any) {
      const msg = err?.response?.data?.name?.[0] || err?.response?.data?.detail || 'Failed to save supplier.';
      setFeedback({ error: msg, success: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = (supplier: Supplier) => {
    setDeleteState({ showModal: true, supplier, deleting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.supplier) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deleteSupplier(deleteState.supplier.id);
      setSuppliers(prev => prev.filter(s => s.id !== deleteState.supplier!.id));
      setDeleteState({ showModal: false, supplier: null, deleting: false });
      setFeedback({ error: '', success: 'Supplier deleted.' });
    } catch {
      setDeleteState(prev => ({ ...prev, deleting: false }));
      setFeedback({ error: 'Failed to delete supplier.', success: '' });
    }
  };

  return {
    isExpired,
    suppliers,
    loading,
    search,
    setSearch,
    filtered,
    showModal,
    editingSupplier,
    form,
    setForm,
    saving,
    feedback,
    setFeedback,
    deleteState,
    handleOpenCreate,
    handleOpenEdit,
    toggleDeliveryDay,
    handleSave,
    handleConfirmDelete,
    handleDelete,
    closeModal: () => setShowModal(false),
    closeDeleteModal: () => setDeleteState({ showModal: false, supplier: null, deleting: false }),
  };
}
